<template>
    <div>
        <ticto-loading v-if="loading" />

        <div class="my-5">
            <div class="mb-5 flex items-center">
                <router-link
                    to="/mobile"
                    class="mr-3 inline-flex items-center text-gray-400 hover:text-gray-300"
                >
                    <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />
                    Voltar
                </router-link>
                <h3 class="text-lg font-medium leading-9 dark:text-white">
                    Inscrição em Massa por CSV
                </h3>
            </div>

            <div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                <form @submit.prevent="handleSubmit">
                    <div class="space-y-4">
                        <div>
                            <label class="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                                Nome do Tópico
                            </label>
                            <input
                                v-model="form.topic_name"
                                type="text"
                                required
                                placeholder="Ex: promo-black-friday"
                                class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Digite o nome do tópico Firebase para inscrever os dispositivos
                            </p>
                        </div>

                        <div>
                            <label class="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                                Arquivo CSV
                            </label>
                            <input
                                @change="handleFileChange"
                                type="file"
                                accept=".csv"
                                required
                                class="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:placeholder-gray-400"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                O CSV deve conter uma coluna com emails dos usuários (uma linha por email)
                            </p>
                        </div>

                        <div v-if="result" class="rounded-lg border p-4" :class="resultClass">
                            <h4 class="mb-2 font-semibold">
                                <span v-if="result.status === 'processing'">
                                    Processamento Iniciado
                                </span>
                                <span v-else>
                                    Resultado da Inscrição
                                </span>
                            </h4>
                            <div class="space-y-2 text-sm">
                                <p v-if="result.status === 'processing'" class="text-blue-600 dark:text-blue-400">
                                    <strong>Status:</strong> Processamento em andamento em segundo plano
                                </p>
                                <p v-if="result.execution_id">
                                    <strong>ID da Execução:</strong>
                                    <code class="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-700">
                                        {{ result.execution_id }}
                                    </code>
                                </p>
                                <p v-if="result.total_emails || result.options?.total_emails">
                                    <strong>Total de emails no CSV:</strong> {{ result.total_emails || result.options?.total_emails || 0 }}
                                </p>
                                <p v-if="result.users_found || result.options?.users_found">
                                    <strong>Usuários encontrados:</strong> {{ result.users_found || result.options?.users_found || 0 }}
                                </p>
                                <p><strong>Dispositivos encontrados:</strong> {{ result.devices_found || result.total_devices || 0 }}</p>
                                <p v-if="result.jobs_dispatched">
                                    <strong>Jobs enviados para processamento:</strong> {{ result.jobs_dispatched }}
                                </p>

                                <div v-if="result.status === 'processing'" class="mt-3 space-y-2">
                                    <div class="rounded bg-blue-50 p-3 dark:bg-blue-900/20">
                                        <p class="text-sm text-blue-800 dark:text-blue-200">
                                            ℹ️ O processamento está sendo executado em segundo plano.
                                            Os dispositivos serão inscritos no tópico <strong>{{ result.topic_name || result.options?.topic_name }}</strong> gradualmente.
                                        </p>
                                    </div>

                                    <div class="rounded bg-gray-50 p-3 dark:bg-gray-800">
                                        <p class="mb-2 font-semibold text-gray-900 dark:text-gray-100">Progresso em tempo real:</p>
                                        <div class="space-y-1 text-sm">
                                            <p class="text-green-600 dark:text-green-400">
                                                <strong>✓ Sucessos:</strong> {{ result.total_successes || 0 }}
                                            </p>
                                            <p class="text-red-600 dark:text-red-400">
                                                <strong>✗ Falhas:</strong> {{ result.total_failures || 0 }}
                                            </p>
                                            <p class="text-gray-600 dark:text-gray-400">
                                                <strong>Processados:</strong> {{ (result.total_successes || 0) + (result.total_failures || 0) }} / {{ result.total_devices || 0 }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <p v-if="result.total_successes !== undefined && result.status !== 'processing'" class="text-green-600 dark:text-green-400">
                                    <strong>Inscrições bem-sucedidas:</strong> {{ result.total_successes }}
                                </p>
                                <p v-if="result.total_failures > 0 && result.status !== 'processing'" class="text-red-600 dark:text-red-400">
                                    <strong>Falhas:</strong> {{ result.total_failures }}
                                </p>

                                <div v-if="result.errors && result.errors.length > 0" class="mt-3">
                                    <p class="font-semibold text-red-600 dark:text-red-400">Detalhes dos erros:</p>
                                    <ul class="ml-4 mt-1 list-disc">
                                        <li v-for="(error, index) in result.errors.slice(0, 10)" :key="index" class="text-xs">
                                            Token: {{ error.token.substring(0, 20) }}... - {{ error.reason }}
                                        </li>
                                        <li v-if="result.errors.length > 10" class="text-xs italic">
                                            ... e mais {{ result.errors.length - 10 }} erros
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="flex gap-3">
                            <submit-button :loading="loading" type="submit">
                                <font-awesome-icon :icon="['fas', 'upload']" class="mr-2" />
                                Processar CSV e Inscrever
                            </submit-button>

                            <router-link
                                to="/mobile"
                                class="inline-flex items-center justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-gray-500"
                            >
                                Cancelar
                            </router-link>
                        </div>
                    </div>
                </form>
            </div>

            <div class="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <h4 class="mb-2 font-semibold text-blue-900 dark:text-blue-300">
                    Como funciona:
                </h4>
                <ul class="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                    <li>1. Prepare um arquivo CSV com uma coluna contendo os emails dos usuários</li>
                    <li>2. Escolha o nome do tópico Firebase onde os dispositivos serão inscritos</li>
                    <li>3. Faça o upload do arquivo - o sistema irá:</li>
                    <li class="ml-4">• Buscar os usuários pelos emails</li>
                    <li class="ml-4">• Localizar os dispositivos mobile desses usuários</li>
                    <li class="ml-4">• Inscrever os tokens no tópico especificado</li>
                    <li>4. Visualize o resultado com estatísticas de sucesso/falha</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useToast } from "vue-toastification";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import SubmitButton from "../../components/forms/SubmitButton.vue";

export default {
    components: {
        TictoLoading,
        SubmitButton,
        FontAwesomeIcon,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            form: {
                topic_name: "",
                csv_file: null,
            },
            result: null,
            progressInterval: null,
        };
    },
    beforeUnmount() {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
        }
    },
    computed: {
        resultClass() {
            if (!this.result) return "";

            if (this.result.status === 'processing') {
                return "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20";
            }

            const failures = this.result.total_failures || 0;
            const successes = this.result.total_successes || 0;

            if (failures === 0 && successes > 0) {
                return "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20";
            }

            if (successes === 0 && failures > 0) {
                return "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20";
            }

            if (failures > 0 && successes > 0) {
                return "border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20";
            }

            return "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20";
        },
    },
    methods: {
        handleFileChange(event) {
            this.form.csv_file = event.target.files[0];
            this.result = null;
            this.stopProgressPolling();
        },
        async fetchProgress(executionId) {
            try {
                const { data } = await axios.get(
                    `/api/mobile-devices/execution/${executionId}/progress`
                );
                this.result = {
                    ...this.result,
                    ...data,
                };

                const totalProcessed = (data.total_successes || 0) + (data.total_failures || 0);
                const totalDevices = data.total_devices || 0;
                const isCompleted = data.status === "completed" || data.status === "failed";
                const allProcessed = totalDevices > 0 && totalProcessed >= totalDevices;

                if (isCompleted || allProcessed) {
                    this.stopProgressPolling();

                    if (data.status === "completed" || allProcessed) {
                        this.toast.success(
                            `Processamento concluído! ${data.total_successes || 0} sucessos, ${data.total_failures || 0} falhas`
                        );
                    } else if (data.status === "failed") {
                        this.toast.error("Processamento falhou");
                    }
                }
            } catch (error) {
                console.error("Error fetching progress:", error);
                this.stopProgressPolling();
            }
        },
        startProgressPolling(executionId) {
            this.stopProgressPolling();
            this.progressInterval = setInterval(() => {
                this.fetchProgress(executionId);
            }, 3000);
        },
        stopProgressPolling() {
            if (this.progressInterval) {
                clearInterval(this.progressInterval);
                this.progressInterval = null;
            }
        },
        handleSubmit() {
            if (!this.form.csv_file) {
                this.toast.error("Por favor, selecione um arquivo CSV");
                return;
            }

            if (!this.form.topic_name.trim()) {
                this.toast.error("Por favor, informe o nome do tópico");
                return;
            }

            this.loading = true;
            this.result = null;

            const formData = new FormData();
            formData.append("topic_name", this.form.topic_name);
            formData.append("csv_file", this.form.csv_file);

            axios
                .post("/api/mobile-devices/topics/bulk-subscribe", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(({ data }) => {
                    this.result = data;

                    if (data.status === 'processing' && data.execution_id) {
                        this.toast.success(
                            `${data.devices_found} dispositivos serão processados em ${data.jobs_dispatched} lotes`
                        );
                        this.startProgressPolling(data.execution_id);
                    } else if (data.total_failures === 0) {
                        this.toast.success(
                            `${data.total_successes} dispositivos inscritos com sucesso!`
                        );
                    } else if (data.total_successes === 0) {
                        this.toast.error("Falha ao inscrever dispositivos");
                    } else {
                        this.toast.warning(
                            `${data.total_successes} inscritos, ${data.total_failures} falharam`
                        );
                    }

                    this.loading = false;
                })
                .catch(({ response }) => {
                    const message =
                        response?.data?.message ||
                        "Ocorreu um erro ao processar o CSV";
                    this.toast.error(message);
                    this.loading = false;
                });
        },
    },
};
</script>
